import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';

import { Template } from '../services/TemplateService/Template.type';
import usePublicTemplate from './usePublicTemplate';
import useRouterMatch from './useRouterMatch';

type AccessError = 'not_found' | 'access_denied' | 'generic_error' | null;
type AccessState = 'idle' | 'checking_public' | 'duplicating' | 'error';

export const useTemplateAccessHandler = ({
  templateId,
  isTemplateOwner,
  privateTemplateError,
  template,
  isFetchingTemplate,
}: {
  templateId: string | null;
  isTemplateOwner: boolean | undefined;
  privateTemplateError: AxiosError | null;
  template?: Template;
  isFetchingTemplate?: boolean;
}) => {
  const { urlTenantId } = useRouterMatch();

  const [accessState, setAccessState] = useState<AccessState>('idle');
  const [accessError, setAccessError] = useState<AccessError>(null);

  const {
    template: publicTemplate,
    isFetchingTemplate: isFetchingPublicTemplate,
    error: publicTemplateError,
  } = usePublicTemplate({
    templateId,
    templateTenantId: urlTenantId,
    isTemplateOwner: isTemplateOwner ?? false,
    isFetchingRawTemplate: isFetchingTemplate,
  });

  // Handle template access logic
  useEffect(() => {
    if (!templateId) {
      setAccessState('idle');
      setAccessError(null);
      return;
    }

    // Wait for template to be loaded before making access decisions
    if (isFetchingTemplate) {
      return;
    }

    // If user is the template owner, they already have access via private template
    if (isTemplateOwner === true) {
      setAccessState('idle');
      setAccessError(null);
      return;
    }

    // If you don't have private access, check for public template access
    setAccessState('checking_public');
  }, [
    templateId,
    isTemplateOwner,
    privateTemplateError?.response?.status,
    isFetchingTemplate,
  ]);

  // Handle public template access results
  useEffect(() => {
    // If user is template owner, they already have access via private template
    if (isTemplateOwner === true) {
      return;
    }

    // Wait for raw template to finish fetching before processing public template
    if (isFetchingTemplate) {
      return;
    }

    if (accessState !== 'checking_public' || isFetchingPublicTemplate) {
      return;
    }
    // Handle public template fetch error
    if (publicTemplateError) {
      setAccessState('error');

      if (publicTemplateError === 404) {
        setAccessError('not_found');
      } else if (publicTemplateError === 403) {
        setAccessError('access_denied');
      } else {
        setAccessError('generic_error');
      }
      return;
    }

    // Check if we have a template to work with
    const templateToProcess = template || publicTemplate;
    if (!templateToProcess) {
      return;
    }

    // Handle template sharing restrictions
    if (templateToProcess.isBlockedFromSharing) {
      setAccessState('error');
      setAccessError('access_denied');
    } else {
      // Template is available but user is not owner - show copy modal
      setAccessState('idle');
      // Note: The copy lesson modal will be shown by the parent component
      // based on the combination of publicTemplate being available and isTemplateOwner being false
    }
  }, [
    accessState,
    isFetchingPublicTemplate,
    publicTemplateError,
    template,
    publicTemplate,
    isTemplateOwner,
    isFetchingTemplate,
  ]);

  return {
    accessState,
    accessError,
    publicTemplate: isTemplateOwner ? template : publicTemplate,
    showErrorModal: accessError !== null,
  };
};
